import React from "react";
import { PageHeader } from "./components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FolderOpen, Users, AlertCircle, ArrowRight, Briefcase } from "lucide-react";
import { useDespachos } from "@/hooks/useDespachos";
import { useNavigate } from "react-router-dom";

const DespachosDashboard = () => {
    const { dashboardMetrics } = useDespachos();
    const navigate = useNavigate();

    const stats = [
        {
            title: "Procesos Activos",
            value: dashboardMetrics?.activeProcesses || 0,
            subtitle: "En curso actualmente",
            icon: FolderOpen,
            gradient: "from-blue-500 to-indigo-600",
            bgLight: "bg-blue-50",
        },
        {
            title: "Clientes Totales",
            value: dashboardMetrics?.totalClients || 0,
            subtitle: "Base de datos de clientes",
            icon: Users,
            gradient: "from-emerald-500 to-teal-600",
            bgLight: "bg-emerald-50",
        },
        {
            title: "Trámites Pendientes",
            value: dashboardMetrics?.pendingTramites || 0,
            subtitle: "Requieren atención",
            icon: AlertCircle,
            gradient: "from-amber-500 to-orange-600",
            bgLight: "bg-amber-50",
        },
    ];

    const quickLinks = [
        { label: "Ver Clientes", path: "/dashboard/despachos/clientes" },
        { label: "Ver Procesos", path: "/dashboard/despachos/procesos" },
        { label: "Ver Trámites", path: "/dashboard/despachos/tramites" },
    ];

    return (
        <div className="space-y-6">
            {dashboardMetrics.urgentTasksCount > 0 && (
                <Card className="border-amber-200 bg-amber-50/50 shadow-sm">
                    <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                                <AlertCircle className="h-5 w-5" />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-amber-900">¡Acción Requerida! Tareas por Vencer</h3>
                                <p className="text-xs text-amber-700">Tienes {dashboardMetrics.urgentTasksCount} tareas que vencen pronto. Revisa tu estrategia defensiva.</p>
                            </div>
                        </div>
                        <button 
                            onClick={() => navigate("/dashboard/despachos/tareas")}
                            className="text-xs font-bold text-amber-700 hover:underline flex items-center gap-1"
                        >
                            Ver Tareas <ArrowRight className="h-3 w-3" />
                        </button>
                    </CardContent>
                </Card>
            )}
            <PageHeader
                title="Gestión de Despachos"
                description="Bienvenido al panel de administración de despachos."
                icon={Briefcase}
            />

            <div className="grid gap-5 md:grid-cols-3">
                {stats.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                        <Card key={index} className={`border border-gray-100/80 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 animate-fade-in-up stagger-${index + 1}`}>
                            <CardHeader className="flex flex-row items-center justify-between pb-3">
                                <CardTitle className="text-sm font-medium text-gray-500">
                                    {stat.title}
                                </CardTitle>
                                <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-md`}>
                                    <Icon className="h-4 w-4 text-white" />
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="text-3xl font-bold text-gray-900">{stat.value}</div>
                                <p className="text-xs text-gray-400 mt-1">{stat.subtitle}</p>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            <Card className="border border-gray-100/80 animate-fade-in-up stagger-4">
                <CardContent className="p-8">
                    <div className="text-center mb-6">
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                            <FolderOpen className="h-6 w-6 text-gray-400" />
                        </div>
                        <p className="text-gray-500 text-sm">Selecciona una opción del menú lateral para comenzar a gestionar tu despacho.</p>
                    </div>
                    <div className="flex flex-wrap gap-3 justify-center">
                        {quickLinks.map((link) => (
                            <button
                                key={link.path}
                                onClick={() => navigate(link.path)}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-legal-blue bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors">
                                {link.label}
                                <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default DespachosDashboard;
